import Box from '@mui/material/Box';
import MuiGridWithConnector from '../MuiGridWithConnector.tsx';
import FieldNames from '../../defaults/FieldNames.json';
import { getAppConfig } from '../../utils/ReduxHelper';
import {
    createContext,
    createRef,
    type RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import ColorConstants from '../../defaults/ColorConstants';
import { AppBar, IconButton, Toolbar, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch, useSelector } from 'react-redux';
import {
    setParentRowEditedCells,
    setShowMap,
    setSubRowEditedCells,
} from '../../redux/slices/displayStateSlice';
import { useNavigate } from 'react-router-dom';
import RestoreIcon from '@mui/icons-material/Restore';
import {
    type DataGridProps,
    type GridColumnVisibilityModel,
    type GridFilterModel,
    type GridRowId,
    useGridApiRef,
} from '@mui/x-data-grid';
import type { DataGridPremiumProps, GridDataSource } from '@mui/x-data-grid-premium';
import {
    type BulkEditingContextValue,
    configureDataSourceAndFields,
    handleDiscardChanges,
    handleGetParentRowClassName,
    handleGetRowClassName,
    handleProcessRowUpdate,
    type UnsavedChanges,
} from '../../utils/BulkEditHelper.tsx';
import type { DataGridProProps } from '@mui/x-data-grid-pro';
import GridRowEditableDetail, {
    type GridRowEditableDetailHandle,
} from '../GridRowEditableDetail/GridRowEditableDetail.tsx';
import Button from '@mui/material/Button';
import SaveIcon from '@mui/icons-material/Save';
import {
    bulkEditFeatureFields,
    bulkEditFundingFields,
} from '../../defaults/BulkEditConfiguration.tsx';

const { WhiteAlien } = ColorConstants;

import BulkEditActionsCell from '../BulkEditActionsCell/BulkEditActionsCell.tsx';
import FieldsNames from '../../defaults/FieldNames.json';
import { setFilterActionsDisabled } from '../../redux/slices/globalContextSlice.jsx';
import FeatureService from '../../services/FeatureService';
import ErrorService from '../../services/ErrorService';
import AuditLogService from '../../services/AuditLogService';
import type {RootState} from "../../redux/store.tsx";

const BulkEditingContext = createContext<BulkEditingContextValue | null>(null);

export default function BulkEdit() {
    const userInfo = useSelector((state: RootState) => state.globalContext.userInfo);

    const [dataSource, setDataSource] = useState<GridDataSource | null>(null);
    const gridColumns = useRef<any[]>([]);
    const config: any = getAppConfig();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const handleClose = () => {
        dispatch(setShowMap(true));
        navigate('/home');
    };

    const headerHeight = useSelector((state) => state.displayState.headerHeight);

    const detailPanelRefs = useRef<Map<GridRowId, RefObject<GridRowEditableDetailHandle>>>(
        new Map()
    ).current;

    /**
     * Lifted state: persists detail-panel unsaved changes across
     * collapse / expand cycles.  Each detail panel syncs its
     * unsavedChangesRef here on every mutation and on unmount,
     * and reads it back as initial state on (re-)mount.
     */
    const detailUnsavedChangesStore = useRef<Map<GridRowId, UnsavedChanges>>(new Map()).current;

    const syncDetailUnsavedChanges = useCallback(
        (rowId: GridRowId, changes: UnsavedChanges) => {
            if (
                Object.keys(changes.unsavedRows).length === 0 &&
                Object.keys(changes.rowsBeforeChange).length === 0
            ) {
                detailUnsavedChangesStore.delete(rowId);
            } else {
                detailUnsavedChangesStore.set(rowId, changes);
            }
        },
        [detailUnsavedChangesStore]
    );

    const apiRef = useGridApiRef();
    const dataSourceRef = useRef(null);
    const [hasUnsavedRows, setHasUnsavedRows] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const unsavedChangesRef = useRef<UnsavedChanges>({
        unsavedRows: {},
        rowsBeforeChange: {},
    });
    const [filterModel, setFilterModel] = useState<GridFilterModel | null>(null);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
        _internalWhereClause: false,
    });

    const definitionExpression = useSelector((state) => state?.filter?.definitionExpression);

    const parentRowEditedCells = useSelector((state) => state.displayState.parentRowEditedCells);

    /**
     * Stable callback for applying derived funding attributes to a parent row.
     * Accepts the rowId so it can be bound per-detail-panel without creating
     * a new closure inside getDetailPanelContent (which would re-render panels).
     */
    const applyParentFeatureUpdate = useCallback(
        (rowId: GridRowId, derivedAttrs: Record<string, any>) => {
            const currentRow = apiRef.current?.getRow(rowId);
            if (!currentRow) return;

            const updatedRow = { ...currentRow, ...derivedAttrs };
            handleProcessRowUpdate(updatedRow, currentRow, unsavedChangesRef, setHasUnsavedRows);
            apiRef.current?.updateRows([updatedRow]);
        },
        [apiRef, unsavedChangesRef]
    );

    const getDetailPanelContent: DataGridProps['getDetailPanelContent'] = useCallback(
        (params: any) => {
            const rowId = params.id;
            if (!detailPanelRefs.has(rowId)) {
                detailPanelRefs.set(rowId, createRef<GridRowEditableDetailHandle>());
            }
            const detailRef = detailPanelRefs.get(rowId);

            return (
                <GridRowEditableDetail
                    ref={detailRef}
                    joinId={params.row[FieldNames.featureFields.joinID]}
                    joinFeature={params.row[FieldNames.featureFields.joinFeature]}
                    fields={bulkEditFundingFields}
                    layerId={config.funding[config.env].layerId}
                    joinIdField={FieldNames.fundingFields.joinID}
                    joinFeatureField={FieldNames.fundingFields.joinFeature}
                    parentFeature={params.row}
                    onParentFeatureUpdate={applyParentFeatureUpdate}
                    parentRowId={rowId}
                    initialUnsavedChanges={detailUnsavedChangesStore.get(rowId)}
                    onUnsavedChangesSync={syncDetailUnsavedChanges}
                />
            );
        },
        [
            detailPanelRefs,
            applyParentFeatureUpdate,
            detailUnsavedChangesStore,
            syncDetailUnsavedChanges,
        ]
    );

    const handleDetailPanelExpansionChange = useCallback<
        NonNullable<DataGridProProps['onDetailPanelExpandedRowIdsChange']>
    >((_newExpandedRowIds) => {
        // Keep refs and lifted state alive so unsaved changes
        // in collapsed panels survive and can be saved/discarded.
    }, []);

    const processRowUpdate = useCallback<NonNullable<DataGridPremiumProps['processRowUpdate']>>(
        (newRow, oldRow) => {
            return handleProcessRowUpdate(newRow, oldRow, unsavedChangesRef, setHasUnsavedRows);
        },
        []
    );

    const discardChanges = useCallback(() => {
        Array.from(detailPanelRefs.values()).map((ref) => {
            return ref.current?.discardChanges();
        });

        // Clear the lifted detail-panel state
        detailUnsavedChangesStore.clear();

        // Reenable the filter panel after discarding changes
        dispatch(setFilterActionsDisabled(false));

        // Clear cell-based change styles
        dispatch(
            setSubRowEditedCells({
                existingRow: [],
                newRow: [],
                changesSavedToParent: true,
            })
        );

        dispatch(
            setParentRowEditedCells({
                existingRow: {},
                newRow: {},
            })
        );

        return handleDiscardChanges(unsavedChangesRef, setHasUnsavedRows, apiRef);
    }, [apiRef, detailUnsavedChangesStore]);

    /**
     * Cascade a single parent row discard to its related funding detail panel.
     * Called by BulkEditActionsCell after restoring the parent row.
     */
    const handleRowDiscard = useCallback(
        (rowId: GridRowId) => {
            // If the detail panel is currently mounted, discard its changes
            const detailRef = detailPanelRefs.get(rowId);
            detailRef?.current?.discardChanges();

            // Also clear the lifted state so a re-mount starts clean
            detailUnsavedChangesStore.delete(rowId);
        },
        [detailPanelRefs, detailUnsavedChangesStore]
    );

    // Stable ref so the renderCell closure (captured once in useEffect)
    // always invokes the latest handleRowDiscard without stale closures.
    const handleRowDiscardRef = useRef(handleRowDiscard);
    handleRowDiscardRef.current = handleRowDiscard;

    const saveChanges = useCallback(async () => {
        try {
            // Persist updates in the database
            setIsSaving(true);

            const attributeSavePromises = Object.values(unsavedChangesRef.current.unsavedRows).map(
                (row) => {
                    const att = Object.assign({}, row);
                    delete att.rowId;
                    return FeatureService.updateFeature(row)
                        .catch((error) => {
                            ErrorService.addErrorLogEntry(
                                error
                            );
                        })
                        .finally(() => {
                            const changeString = Object.entries(att)
                                .map(([key, value]) => `${key}: ${value}`)
                                .join(', ');

                            AuditLogService.addAuditLogEntry(
                                att,
                                att[FieldNames.featureFields.joinID],
                                att[FieldNames.featureFields.joinFeature],
                                userInfo[FieldNames.userFields.nifcUsername],
                                `Attribute Row Modified: ${changeString}`
                            );
                        });
                }
            );

            const detailSavePromises = Array.from(detailPanelRefs.values()).map((ref) => {
                return ref.current?.saveChanges();
            });

            await Promise.all([...detailSavePromises, ...attributeSavePromises]);

            setIsSaving(false);

            setHasUnsavedRows(false);
            unsavedChangesRef.current = {
                unsavedRows: {},
                rowsBeforeChange: {},
            };

            // Clear lifted detail-panel state after successful save
            detailUnsavedChangesStore.clear();

            // Clear cell-based change-styles
            dispatch(
                setSubRowEditedCells({
                    existingRow: [],
                    newRow: [],
                    changesSavedToParent: true,
                })
            );

            dispatch(
                setParentRowEditedCells({
                    existingRow: {},
                    newRow: {},
                })
            );

            // Re-enable the filter panel after saving is complete
            dispatch(setFilterActionsDisabled(false));
        } catch (error) {
            setIsSaving(false);
        }
    }, [apiRef, detailPanelRefs]);

    const getRowClassName = useCallback<NonNullable<DataGridPremiumProps['getRowClassName']>>(
        ({ id }) => {
            return handleGetRowClassName(id, unsavedChangesRef);
        },
        []
    );

    const getCellClassName = (params) => {
        return handleGetParentRowClassName(params.id, params.field, parentRowEditedCells);
    };

    const bulkEditingContextValue = useMemo<BulkEditingContextValue>(
        () => ({
            unsavedChangesRef,
            setHasUnsavedRows,
        }),
        [unsavedChangesRef]
    );

    useEffect(() => {
        const initSetup = async () => {
            const { dataSource, colDefs } = await configureDataSourceAndFields(
                config.actionAttributes[config.env].layerId,
                bulkEditFeatureFields,
                '',
                FieldsNames.featureFields.objectID
            );

            gridColumns.current = [
                // because we have a completely external filter panel, use a column to trigger the grid data fetching when that expression changes
                {
                    field: '_internalWhereClause',
                    type: 'string',
                },
                {
                    field: 'actions',
                    type: 'actions',
                    renderCell: (params: any) => (
                        <BulkEditActionsCell
                            {...params}
                            context={bulkEditingContextValue}
                            onRowDiscard={(rowId) => handleRowDiscardRef.current(rowId)}
                        />
                    ),
                },
                ...colDefs,
            ];
            setDataSource(dataSource);
        };

        if (config) {
            initSetup();
        }
    }, [config]);

    useEffect(() => {
        setFilterModel({
            items: [
                {
                    field: '_internalWhereClause',
                    operator: 'equals',
                    value: definitionExpression ?? '',
                },
            ],
        });
    }, [definitionExpression]);

    return (
        <Box
            sx={{
                height: '100%',
                width: '100%',
                minWidth: '10px',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            <AppBar
                sx={{ position: 'relative', flexShrink: 0 }}
                style={{ backgroundColor: ColorConstants.BlueAlien, marginTop: headerHeight }}
            >
                <Toolbar>
                    <IconButton
                        edge='start'
                        color='inherit'
                        onClick={handleClose}
                        aria-label='close'
                    >
                        <CloseIcon style={{ fill: WhiteAlien }} />
                    </IconButton>
                    <Typography
                        sx={{ ml: 2, flex: 1 }}
                        variant='h6'
                        component='div'
                        style={{ color: WhiteAlien }}
                    >
                        Bulk Edit
                    </Typography>
                </Toolbar>
            </AppBar>
            <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                <Button onClick={saveChanges} startIcon={<SaveIcon />}>
                    <span>Save</span>
                </Button>
                <Button disabled={isSaving} onClick={discardChanges} startIcon={<RestoreIcon />}>
                    Discard all changes
                </Button>
            </div>
            {dataSource && (
                <BulkEditingContext.Provider value={bulkEditingContextValue}>
                    <MuiGridWithConnector
                        featureColumns={gridColumns.current}
                        dataSource={dataSource}
                        apiRef={apiRef}
                        processRowUpdate={processRowUpdate}
                        isSaving={isSaving}
                        getRowClassName={getRowClassName}
                        getCellClassName={getCellClassName}
                        getDetailPanelContent={getDetailPanelContent}
                        onDetailPanelExpandedRowIdsChange={handleDetailPanelExpansionChange}
                        columnVisibilityModel={columnVisibilityModel}
                        filterModel={filterModel}
                    ></MuiGridWithConnector>
                </BulkEditingContext.Provider>
            )}
        </Box>
    );
}
